import React, { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  X, 
  Minus, 
  Phone,
  Video,
  MoreHorizontal,
  Smile,
  Paperclip,
  Send,
  Image as ImageIcon,
  ArrowDown,
  Check,
  CheckCheck
} from 'lucide-react';
import { OnlineStatus } from './online-status';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useCall } from '@/components/calls/CallProvider';
import { useToast } from '@/components/ui/use-toast';
import { db, rtdb } from '@/lib/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  updateDoc,
  doc,
  getDoc
} from 'firebase/firestore';
import { ref, set, get } from 'firebase/database';
import { formatDistanceToNow } from 'date-fns';
import { EmailAPI } from '@/services/emailAPI';
import { notificationService } from '@/services/notificationService';

interface ChatContact {
  id: string;
  name: string;
  photoURL?: string;
  isOnline: boolean;
  lastMessage?: string;
  unreadCount: number;
  timestamp?: Date;
}

interface Message {
  id: string;
  senderId: string;
  content: string;
  timestamp: Date;
  seen: boolean;
  readAt?: string | null;
  type?: 'text' | 'image' | 'file';
  mediaUrl?: string;
}

interface FloatingChatWindowProps {
  contact: ChatContact;
  isMinimized: boolean;
  position: number;
  onClose: () => void;
  onMinimize: () => void;
  onRestore: () => void;
}

export function FloatingChatWindow({
  contact,
  isMinimized,
  position,
  onClose,
  onMinimize,
  onRestore
}: FloatingChatWindowProps) {
  const { user } = useAuth();
  const { startCall, currentCall, isInCall } = useCall();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeCallPeerId = React.useMemo(() => {
    if (!currentCall || !user) return null;
    return currentCall.caller.id === user.uid ? currentCall.receiver.id : currentCall.caller.id;
  }, [currentCall, user]);

  const isThisContactInActiveCall = Boolean(activeCallPeerId && activeCallPeerId === contact.id);
  const isOtherConversationActive = Boolean(isInCall && activeCallPeerId && activeCallPeerId !== contact.id);

  // Position calculation
  const rightOffset = 24 + (position * 320); // 320px width + gap
  
  useEffect(() => {
    if (!user || !contact.id) return;

    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('participants', 'array-contains', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            senderId: data.senderId,
            content: data.content || '',
            timestamp: data.createdAt?.toDate() || new Date(),
            seen: data.seen || false,
            readAt: data.readAt || null,
            type: data.type || 'text',
            mediaUrl: data.mediaUrl
          } as Message;
        })
        .filter(msg => {
          const participants = snapshot.docs
            .find(doc => doc.id === msg.id)
            ?.data().participants || [];
          return participants.includes(user.uid) && participants.includes(contact.id);
        })
        .reverse(); // Show oldest first

      setMessages(messagesData);
      scrollToBottom();
    });

    return () => unsubscribe();
  }, [user, contact.id]);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShowScrollButton(!isNearBottom);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user) return;

    const messageData = {
      senderId: user.uid,
      receiverId: contact.id,
      content: newMessage.trim(),
      participants: [user.uid, contact.id],
      createdAt: serverTimestamp(),
      seen: false,
      type: 'text',
      senderName: user.displayName || user.email,
      senderPhoto: user.photoURL
    };

    try {
      await addDoc(collection(db, 'messages'), messageData);
      setNewMessage('');
      scrollToBottom('smooth');

      // Send in-app notification
      await notificationService.createNotification({
        userId: contact.id,
        type: 'new_message',
        data: {
          senderId: user.uid,
          senderName: user.displayName || user.email || 'Someone',
          senderPhoto: user.photoURL,
          messagePreview: newMessage.trim().substring(0, 50),
          // Deep-link so notifications can open the exact conversation
          actionUrl: `/messages?user=${user.uid}`
        }
      });

      // Send email notification
      try {
        const receiverDoc = await getDoc(doc(db, 'users', contact.id));
        if (receiverDoc.exists()) {
          const receiverData = receiverDoc.data();
          if (receiverData.email) {
            const messagePreview = newMessage.trim().length > 150 
              ? newMessage.trim().substring(0, 150) + '...'
              : newMessage.trim();

            await EmailAPI.sendNewMessageEmail(
              receiverData.email,
              contact.name || 'there',
              user.displayName || user.email || 'Someone',
              messagePreview
            );
          }
        }
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't fail the message send if email fails
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive'
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFocus = () => {
    if (isMinimized) {
      onRestore();
    }
  };

  const handleAudioCall = async () => {
    if (!user) return;

    await startCall(
      contact.id,
      contact.name,
      contact.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${contact.name}`,
      'audio'
    );
  };

  const handleVideoCall = async () => {
    if (!user) return;

    await startCall(
      contact.id,
      contact.name,
      contact.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${contact.name}`,
      'video'
    );
  };

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 z-40 cursor-pointer"
        style={{ right: `${rightOffset}px` }}
        onClick={onRestore}
      >
        <Card className="w-64 bg-white shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200">
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-t-lg">
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={contact.photoURL} />
                  <AvatarFallback className="bg-white/20 text-white text-sm">
                    {contact.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <OnlineStatus isOnline={contact.isOnline || false} size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{contact?.name || 'Unknown User'}</p>
                {contact?.unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white text-xs h-4 px-1 mt-1">
                    {contact.unreadCount} new
                  </Badge>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="fixed bottom-6 z-40"
      style={{ right: `${rightOffset}px` }}
    >
      <Card className="w-80 h-96 bg-white shadow-2xl border border-gray-200 flex flex-col overflow-hidden rounded-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={contact.photoURL} />
                  <AvatarFallback className="bg-white/20 text-white">
                    {contact.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <OnlineStatus isOnline={contact?.isOnline || false} size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{contact?.name || 'Unknown User'}</p>
                <p className="text-xs text-white/80">
                  {contact?.isOnline ? 'Active now' : 'Offline'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 h-7 w-7 p-0 disabled:opacity-50"
                onClick={() => void handleAudioCall()}
                disabled={isOtherConversationActive || isThisContactInActiveCall}
                title={isOtherConversationActive ? 'Another call is active' : 'Start audio call'}
              >
                <Phone className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 h-7 w-7 p-0 disabled:opacity-50"
                onClick={() => void handleVideoCall()}
                disabled={isOtherConversationActive || isThisContactInActiveCall}
                title={isOtherConversationActive ? 'Another call is active' : 'Start video call'}
              >
                <Video className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 h-7 w-7 p-0"
                onClick={onMinimize}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 h-7 w-7 p-0"
                onClick={onClose}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div 
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-3 bg-gray-50"
          onScroll={handleScroll}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f8fafc' fill-opacity='0.6'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        >
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Avatar className="h-16 w-16 mb-3">
                <AvatarImage src={contact.photoURL} />
                <AvatarFallback className="bg-gradient-to-r from-blue-500 to-blue-700 text-white text-xl">
                  {contact.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <p className="text-gray-600 text-sm mb-1">Say hello to {contact.name}</p>
              <p className="text-gray-400 text-xs">Start your conversation</p>
            </div>
          ) : (
            <div className="space-y-2">
              {messages.map((message) => {
                const isSender = message.senderId === user?.uid;
                return (
                  <div
                    key={message.id}
                    className={cn(
                      "flex",
                      isSender ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[70%] rounded-2xl px-3 py-2 text-sm",
                        isSender
                          ? "bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-br-md"
                          : "bg-gradient-to-r from-blue-400 to-blue-600 text-white rounded-bl-md"
                      )}
                    >
                      <p className="break-words text-white">{message.content}</p>
                      <div className={cn(
                        "flex flex-col gap-0.5 mt-1",
                        isSender ? "items-end" : "items-start"
                      )}>
                        <div className={cn(
                          "flex items-center gap-1",
                          isSender ? "text-white/70" : "text-white/80"
                        )}>
                          <span className="text-xs">
                            {formatDistanceToNow(message.timestamp, { addSuffix: true })}
                          </span>
                          {isSender && (
                            message.seen ? 
                              <CheckCheck className="h-3 w-3" /> : 
                              <Check className="h-3 w-3" />
                          )}
                        </div>
                        {isSender && message.seen && message.readAt && (
                          <span className="text-xs text-white/90">
                            Seen {formatDistanceToNow(new Date(message.readAt), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <Button
            variant="outline"
            size="sm"
            className="absolute bottom-20 right-4 h-8 w-8 p-0 rounded-full shadow-lg bg-white hover:bg-gray-50"
            onClick={() => scrollToBottom('smooth')}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}

        {/* Input Area */}
        <div className="flex-shrink-0 p-3 bg-white border-t border-gray-200">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
              onClick={() => toast({ title: 'Attachments', description: 'Feature coming soon!' })}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            
            <div className="flex-1 relative">
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={handleFocus}
                placeholder="Type a message..."
                className="pr-10 border-gray-300 rounded-full focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1 text-gray-500 hover:text-gray-700 h-6 w-6 p-0"
                onClick={() => toast({ title: 'Emoji', description: 'Feature coming soon!' })}
              >
                <Smile className="h-4 w-4" />
              </Button>
            </div>
            
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              className={cn(
                "h-8 w-8 p-0 rounded-full transition-all duration-200",
                newMessage.trim()
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              )}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}